import React, { useEffect, useLayoutEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { IntlProvider } from 'react-intl';
import {
  addErrorToReport,
  addLogToReport,
  loadLanguagePack
} from '@americanexpress/one-app-ducks';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import childRoutes from '../childRoutes';
import localeFallback from '../../locale/en-US.json';

const DLS_STYLESHEET_HREF =
  'https://www.aexp-static.com/cdaas/dls/packages/@americanexpress/dls/7.9.0/stylesheets/dls-core.min.css';

// Defined outside the component so Helmet receives a stable array reference.
const baseHelmetLinks = [
  { rel: 'preload', as: 'style', href: DLS_STYLESHEET_HREF },
  { rel: 'stylesheet', href: DLS_STYLESHEET_HREF },
  { rel: 'icon', href: 'https://www.americanexpress.com/favicon.ico' }
];

const NewAccountsRoot = ({ languageData, locale, children }) => {
  const dispatch = useDispatch();
  const appUrl = useSelector((state) => state.getIn(['config', 'appUrl']));
  const isNewAccountsHost =
    window.location.hostname === 'localhost' ||
    window.location.hostname.includes('newaccounts');

  useEffect(() => {
    const referrerOrigin = (() => {
      if (!document.referrer) return 'direct';
      try {
        return new URL(document.referrer).origin;
      } catch {
        return 'unknown';
      }
    })();

    dispatch(
      addLogToReport('info', 'NewAccountsRoot page hit', {
        moduleID: 'new-accounts-root',
        event: 'page-hit',
        hostname: window.location.hostname,
        pathname: window.location.pathname,
        referrerOrigin
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- on load
  }, []);

  useLayoutEffect(() => {
    if (isNewAccountsHost || !appUrl) return;
    const redirectTarget = new URL(
      window.location.pathname + window.location.search + window.location.hash,
      appUrl
    );
    window.location.replace(redirectTarget.href);
  }, [appUrl, isNewAccountsHost]);

  if (!isNewAccountsHost) {
    return null;
  }

  const messages =
    Object.entries(languageData).length > 0 ? languageData : localeFallback;

  return (
    <IntlProvider locale={locale} messages={messages}>
      <Helmet
        htmlAttributes={{ lang: locale }}
        meta={[{ name: 'robots', content: 'noindex, nofollow' }]}
        link={baseHelmetLinks}
      />
      {children}
    </IntlProvider>
  );
};

export const TestableNewAccountsRoot = NewAccountsRoot;

NewAccountsRoot.propTypes = {
  children: PropTypes.node,
  languageData: PropTypes.shape({}).isRequired, // no need to restate all the keys in the lang pack
  locale: PropTypes.string.isRequired
};

// Read about childRoutes:
// https://github.com/americanexpress/one-app/blob/main/docs/api/modules/Routing.md#childroutes
NewAccountsRoot.childRoutes = childRoutes;

// Read about appConfig:
// https://github.com/americanexpress/one-app/blob/main/docs/api/modules/App-Configuration.md
/* istanbul ignore next */
if (!global.BROWSER) {
  // eslint-disable-next-line global-require -- require needs to be inside browser check
  NewAccountsRoot.appConfig = require('../appConfig').default;
}

export const loadModuleData = async ({ store: { dispatch } }) => {
  const collectionMethod = 'loadModuleData';
  const moduleID = 'new-accounts-root';

  try {
    const response = await dispatch(
      loadLanguagePack('new-accounts-root', { fallbackLocale: 'en-US' })
    );

    dispatch(
      addLogToReport('info', 'NewAccountsRoot loaded successfully', {
        moduleID,
        collectionMethod,
        status: 'success',
        event: 'module-load'
      })
    );

    return response;
  } catch (error) {
    const normalizedError =
      error instanceof Error ? error : new Error(String(error));
    dispatch(
      addErrorToReport(normalizedError, {
        collectionMethod,
        moduleID
      })
    );
    throw error;
  }
};

NewAccountsRoot.holocron = {
  name: 'new-accounts-root',
  loadModuleData
};

export default oneAppModuleWrapper('new-accounts-root')(NewAccountsRoot);
